import axios from 'axios';
import { getAccessToken, getRefreshToken, storeTokens, clearTokens } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Attach Bearer token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401: try refresh token, then retry the original request
let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/login') && !originalRequest.url.includes('/auth/refresh')) {
      originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshToken = getRefreshToken();
          const headers = {};
          if (refreshToken) headers['Authorization'] = `Bearer ${refreshToken}`;
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken }, { headers });
          // Store new access token
          if (res.data?.access_token) storeTokens(res.data.access_token, null);
          isRefreshing = false;
          refreshQueue.forEach(cb => cb());
          refreshQueue = [];
          return api(originalRequest);
        } catch {
          isRefreshing = false;
          refreshQueue = [];
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }
      return new Promise((resolve) => {
        refreshQueue.push(() => resolve(api(originalRequest)));
      });
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/auth/register', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Machinery
export const machineryApi = {
  getAll: () => api.get('/machinery'),
  getById: (id) => api.get(`/machinery/${id}`),
  create: (data) => api.post('/machinery', data),
  update: (id, data) => api.put(`/machinery/${id}`, data),
  delete: (id) => api.delete(`/machinery/${id}`),
};

// Fuel Logs
export const fuelLogsApi = {
  getAll: (machineryId) => api.get('/fuel-logs', { params: { machinery_id: machineryId } }),
  create: (data) => api.post('/fuel-logs', data),
};

// Maintenance
export const maintenanceApi = {
  getAll: (machineryId) => api.get('/maintenance', { params: { machinery_id: machineryId } }),
  create: (data) => api.post('/maintenance', data),
  getReminders: () => api.get('/maintenance/reminders'),
};

// Stock
export const stockApi = {
  getAll: () => api.get('/stock'),
  create: (data) => api.post('/stock', data),
  update: (id, data) => api.put(`/stock/${id}`, data),
  delete: (id) => api.delete(`/stock/${id}`),
};

// Field Notes
export const fieldNotesApi = {
  getAll: (locationId) => api.get('/field-notes', { params: { location_id: locationId } }),
  create: (data) => api.post('/field-notes', data),
  uploadPhoto: (noteId, formData) => api.post(`/field-notes/${noteId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Prestarts
export const prestartsApi = {
  getAll: (machineryId) => api.get('/prestarts', { params: { machinery_id: machineryId } }),
  create: (data) => api.post('/prestarts', data),
};

// Locations
export const locationsApi = {
  getAll: () => api.get('/locations'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Turf Cuts
export const turfCutsApi = {
  getAll: (locationId) => api.get('/turf-cuts', { params: { location_id: locationId } }),
  create: (data) => api.post('/turf-cuts', data),
  uploadPhoto: (cutId, formData) => api.post(`/turf-cuts/${cutId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Spraying
export const sprayingApi = {
  getAll: (locationId) => api.get('/spraying', { params: { location_id: locationId } }),
  getById: (id) => api.get(`/spraying/${id}`),
  create: (data) => api.post('/spraying', data),
};

// Activity Types
export const activityTypesApi = {
  getAll: () => api.get('/activity-types'),
  create: (data) => api.post('/activity-types', data),
  delete: (id) => api.delete(`/activity-types/${id}`),
};

// Activities
export const activitiesApi = {
  getAll: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
  uploadPhoto: (activityId, formData) => api.post(`/activities/${activityId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Files
export const filesApi = {
  getUrl: (path) => `${API}/files/${path}`,
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getWeather: (lat, lon) => api.get('/weather', { params: { lat, lon } }),
  getCosts: () => api.get('/analytics/costs'),
  getCalendarEvents: (month) => api.get('/calendar/events', { params: { month } }),
  getFarmOverview: () => api.get('/farm-overview'),
};

// Reports
export const reportsApi = {
  getTurfCuts: (startDate, endDate) => api.get('/reports/turf-cuts', { params: { start_date: startDate, end_date: endDate } }),
  getFuel: (startDate, endDate) => api.get('/reports/fuel', { params: { start_date: startDate, end_date: endDate } }),
  getMachinery: () => api.get('/reports/machinery'),
  getActivities: (startDate, endDate) => api.get('/reports/activities', { params: { start_date: startDate, end_date: endDate } }),
  getServices: (startDate, endDate) => api.get('/reports/services', { params: { start_date: startDate, end_date: endDate } }),
};

// Rainfall
export const rainfallApi = {
  getAll: (startDate, endDate) => api.get('/rainfall', { params: { start_date: startDate, end_date: endDate } }),
  create: (data) => api.post('/rainfall', data),
  delete: (id) => api.delete(`/rainfall/${id}`),
  getSummary: () => api.get('/rainfall/summary'),
  fetchWeather: () => axios.get(`${API}/rainfall/fetch-weather`),
};

// Store: Varieties (admin)
export const varietiesApi = {
  getAll: () => api.get('/varieties'),
  create: (data) => api.post('/varieties', data),
  update: (id, data) => api.put(`/varieties/${id}`, data),
  delete: (id) => api.delete(`/varieties/${id}`),
  uploadPhoto: (id, formData) => api.post(`/varieties/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePhoto: (id, path) => api.delete(`/varieties/${id}/photos`, { params: { path } }),
};

// Store: Orders (admin)
export const ordersApi = {
  getAll: (status) => api.get('/orders', { params: { status } }),
  getSummary: () => api.get('/orders/summary'),
  create: (data) => api.post('/orders', { ...data, origin_url: window.location.origin }),
  updateStatus: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  xeroSync: (id) => api.post(`/orders/${id}/xero/sync`),
};

// Xero (admin)
export const xeroApi = {
  status: () => api.get('/xero/status'),
  connect: () => api.post('/xero/connect', { origin_url: window.location.origin }),
  disconnect: () => api.post('/xero/disconnect'),
  updateSettings: (data) => api.put('/xero/settings', data),
  accounts: () => api.get('/xero/accounts'),
};

// Store: Delivery zones (admin)
export const deliveryZonesApi = {
  getAll: () => api.get('/delivery-zones'),
  create: (data) => api.post('/delivery-zones', data),
  update: (id, data) => api.put(`/delivery-zones/${id}`, data),
  delete: (id) => api.delete(`/delivery-zones/${id}`),
};

// Store: Invoices (admin)
export const invoicesApi = {
  getSettings: () => api.get('/invoice-settings'),
  updateSettings: (data) => api.put('/invoice-settings', data),
  testEmail: (to) => api.post('/invoice-settings/test-email', { to }),
  send: (orderId) => api.post(`/orders/${orderId}/invoice/send`, { origin_url: window.location.origin }),
  downloadPdf: (orderId) => api.get(`/orders/${orderId}/invoice.pdf`, { params: { origin_url: window.location.origin }, responseType: 'blob' }),
  markPaid: (orderId, data) => api.put(`/orders/${orderId}/invoice/mark-paid`, data),
};

// Store: Public storefront (no auth)
export const storeApi = {
  getVarieties: () => axios.get(`${API}/public/varieties`),
  getVariety: (slug) => axios.get(`${API}/public/varieties/${slug}`),
  createOrder: (data) => axios.post(`${API}/public/orders`, { ...data, origin_url: window.location.origin }),
  trackOrder: (orderNumber, email) => axios.get(`${API}/public/orders/${encodeURIComponent(orderNumber)}`, { params: { email } }),
  getDeliveryZones: () => axios.get(`${API}/public/delivery-zones`),
  getDeliveryQuote: (suburb, postcode, totalM2) => axios.get(`${API}/public/delivery-quote`, { params: { suburb, postcode, total_m2: totalM2 } }),
  getInvoiceSettings: () => axios.get(`${API}/public/invoice-settings`),
  invoicePdfUrl: (orderNumber, email) => `${API}/public/invoices/${encodeURIComponent(orderNumber)}.pdf?email=${encodeURIComponent(email)}`,
  createInvoiceCheckout: (orderNumber, email) => axios.post(`${API}/public/invoices/${encodeURIComponent(orderNumber)}/checkout`, { email, origin_url: window.location.origin }),
  paymentStatus: (sessionId) => axios.get(`${API}/public/payments/status/${sessionId}`),
  photoUrl: (path) => `${API}/public/files/${path}`,
};

export default api;
